import type { LoginResponse, SessionTenant, SessionUser, StaffLogin, SuperLoginResponse, TenantChoice } from '@rfleet/shared';
import { create } from 'zustand';
import { api, refreshSession, tokenStore } from './api';
import { connectSocket, disconnectSocket } from './socket';

type Realm = 'tenant' | 'super' | null;

interface SessionState {
  status: 'booting' | 'anonymous' | 'authenticated';
  realm: Realm;
  user: SessionUser | null;
  tenant: SessionTenant | null;
  admin: SuperLoginResponse['admin'] | null;
  /** Super-admin support mode: the tenant being viewed. */
  actingTenantId: string | null;

  boot: () => Promise<void>;
  loginStaff: (dto: StaffLogin) => Promise<{ tenantRequired: true; tenants: TenantChoice[] } | { ok: true }>;
  loginSuper: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  reloadMe: () => Promise<void>;
  setActingTenant: (id: string | null) => void;
  can: (perm: string) => boolean;
}

const REALM_KEY = 'rfleet.realm';

export const useSession = create<SessionState>((set, get) => ({
  status: 'booting',
  realm: null,
  user: null,
  tenant: null,
  admin: null,
  actingTenantId: null,

  async boot() {
    tokenStore.setSessionLostHandler(() => set({ status: 'anonymous', realm: null, user: null, tenant: null, admin: null }));
    const ok = await refreshSession();
    if (!ok) {
      set({ status: 'anonymous' });
      return;
    }
    const realm = (localStorage.getItem(REALM_KEY) as Realm) ?? 'tenant';
    if (realm === 'super') {
      set({ status: 'authenticated', realm: 'super', admin: JSON.parse(localStorage.getItem('rfleet.admin') ?? 'null') });
      return;
    }
    await get().reloadMe();
  },

  async reloadMe() {
    try {
      const me = await api<Omit<LoginResponse, 'accessToken' | 'refreshToken' | 'expiresIn' | 'refreshExpiresAt'>>('/auth/me');
      set({ status: 'authenticated', realm: 'tenant', user: me.user, tenant: me.tenant });
      connectSocket();
    } catch {
      tokenStore.clear();
      set({ status: 'anonymous', realm: null, user: null, tenant: null });
    }
  },

  async loginStaff(dto) {
    const res = await api<LoginResponse | { tenantRequired: true; tenants: TenantChoice[] }>('/auth/login', { method: 'POST', json: dto, noRetry: true });
    if ('tenantRequired' in res) return res;
    tokenStore.setAccess(res.accessToken);
    tokenStore.setRefresh(res.refreshToken);
    localStorage.setItem(REALM_KEY, 'tenant');
    set({ status: 'authenticated', realm: 'tenant', user: res.user, tenant: res.tenant, admin: null });
    connectSocket();
    return { ok: true };
  },

  async loginSuper(email, password) {
    const res = await api<SuperLoginResponse>('/super/auth/login', { method: 'POST', json: { email, password }, noRetry: true });
    tokenStore.setAccess(res.accessToken);
    tokenStore.setRefresh(res.refreshToken);
    localStorage.setItem(REALM_KEY, 'super');
    localStorage.setItem('rfleet.admin', JSON.stringify(res.admin));
    set({ status: 'authenticated', realm: 'super', admin: res.admin, user: null, tenant: null });
  },

  async logout() {
    const refreshToken = tokenStore.getRefresh();
    try {
      await api('/auth/logout', { method: 'POST', json: { refreshToken }, noRetry: true });
    } catch {
      /* ignore */
    }
    disconnectSocket();
    tokenStore.clear();
    localStorage.removeItem(REALM_KEY);
    localStorage.removeItem('rfleet.admin');
    set({ status: 'anonymous', realm: null, user: null, tenant: null, admin: null, actingTenantId: null });
  },

  setActingTenant(id) {
    set({ actingTenantId: id });
  },

  can(perm) {
    const s = get();
    if (s.realm === 'super') return true;
    return s.user?.role.permissions.includes(perm) ?? false;
  },
}));
